import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ProgrammePreviewModal } from 'src/component/programme-preview-modal/programme-preview-modal'
import { SessionContext } from 'src/context/session-context'
import { HustlerProgrammeEnrollmentSource, Intensity, Programme, ProgrammeWorkout } from 'src/dao/core-dao'
import { useMyStartedProgrammes, useProgrammeDuration, useProgrammeIntensities, useStartProgramme } from 'src/hook/use-programmes'
import { programmeUtil } from 'src/util/programme-util'

interface UsePreviewSeriesReturnType {
  previewSeriesModal: React.ReactElement
  startSeriesPreview: (p: Programme) => void
}

export const usePreviewSeries = (
  seriesWorkout?: ProgrammeWorkout,
  enrollmentSource: HustlerProgrammeEnrollmentSource = 'library',
): UsePreviewSeriesReturnType => {
  const [previewProgramme, setPreviewProgramme] = useState<Programme>()
  const { data: programmeIntensities = [] } = useProgrammeIntensities()
  const { data: startedProgrammes = [] } = useMyStartedProgrammes()
  const { hasActiveSubscription, toggleSubscriptionModal } = useContext(SessionContext)

  const navigate = useNavigate()

  const startProgrammeMutation = useStartProgramme()

  const getIntensity = (intensityId: string | undefined): Intensity | undefined => {
    if (!intensityId) {
      return undefined
    }
    return programmeIntensities.find((i) => i.id === intensityId)
  }

  const selectProgramme = (p: Programme): void => {
    if (programmeUtil.isProgrammeStarted(p.id, startedProgrammes)) {
      navigate(`/programmes/${p.id}?week=${seriesWorkout?.onWeek}`, {
        state: {
          seriesWorkout,
        },
        replace: false,
      })
      return
    }
    setPreviewProgramme(p)
  }

  const onStartProgramme = (): void => {
    if (!previewProgramme) {
      return
    }
    if (!hasActiveSubscription) {
      toggleSubscriptionModal()
      return
    }
    startProgrammeMutation.mutate(
      { programmeId: previewProgramme.id, enrollmentSource },
      {
        onSuccess: () =>
          navigate(`/programmes/${previewProgramme.id}`, {
            replace: false,
          }),
      },
    )
  }

  const { data: previewProgrammeDuration } = useProgrammeDuration(previewProgramme?.id ?? '')

  return {
    previewSeriesModal: (
      <ProgrammePreviewModal
        programme={previewProgramme}
        toggle={() => setPreviewProgramme(undefined)}
        intensity={getIntensity(previewProgramme?.intensityId)}
        programmeDuration={previewProgrammeDuration}
        onStartProgramme={onStartProgramme}
      />
    ),
    startSeriesPreview: selectProgramme,
  }
}
